import { FC, CSSProperties, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { TextAreaInput } from 'components/Inputs/TextAreaInput';
import SelectComponent from 'components/Inputs/SelectComponent/SelectComponent';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import { TableActionsButtons } from 'components/RequestsComponents/TableActionsButtons';
import { DatePickerComponent } from 'components/Inputs/DatePickerComponents/DatePickerComponent';
import { ISelectStyleProps } from 'components/Inputs/SelectComponent/types';
import RequestHeader from 'components/RequestsComponents/RequestHeader';
import EntityOption from 'components/Core/EntityOption';
import { IProductRequestData } from 'stores/requests/types';
import { IBrandRef } from 'types/common';
import { productRequestFormTranslations } from './translations';
import { ProductRequestFormTransFields } from './types';
import styles from './productRequestForm.module.scss';

const selectStyles: ISelectStyleProps = {
  controlStyles: {
    border: '1px solid red',
  },
};

const buttonStyleProp: CSSProperties = {
  width: '100%',
};

export const ProductRequestForm: FC = observer(() => {
  const {
    requestsStore: { productRequestData },
  } = useStore();

  const { t } = useMyTranslations<ProductRequestFormTransFields>([productRequestFormTranslations.name]);

  const useFormMethods = useForm<IProductRequestData>({
    defaultValues: productRequestData || undefined,
  });

  const { getValues } = useFormMethods;

  const renderBrandOption = useCallback((data: IBrandRef) => {
    return <EntityOption image={data.logoUrl} entityType='brands' name={data.name} />;
  }, []);

  return productRequestData ? (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <RequestHeader>
        <TableActionsButtons id={productRequestData.id} entity='products' status={productRequestData.status} />
      </RequestHeader>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.formSection}`}>
            <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
            <ControlledInput
              name='name'
              type='text'
              label={t('name')}
              disabled
              classNameProp={productRequestData.changedFieldsNames.includes('name') ? styles.highlight : undefined}
            />
            <TextAreaInput
              name='description'
              label={t('description')}
              placeholder={t('description')}
              disabled
              classNameProp={
                productRequestData.changedFieldsNames.includes('description') ? styles.highlight : undefined
              }
            />
            <ControlledInput
              name='price'
              type='number'
              label={t('price')}
              disabled
              icon={<p className={styles.currency}>{getValues('currencyCode')}</p>}
              iconPosition='right'
            />
            <SelectComponent
              name='categories'
              label={t('categories')}
              placeholder=''
              options={[]}
              // TODO replace name for categoryName as from IProductsCategoryData interaface
              optionLabel='name'
              optionValue='id'
              isDisabled
              isMulti
              styleProps={productRequestData.changedFieldsNames.includes('categories') ? selectStyles : undefined}
            />
            <AutocompleteSelect
              name='brand'
              label={t('brand')}
              placeholder=''
              data={[]}
              customSingleValue={renderBrandOption}
              isDisabled
            />
            <ControlledInput name='discountType' type='text' label={t('discountType')} placeholder='' disabled />
            <ControlledInput
              name='discountValue'
              type='number'
              label={t('discountValue')}
              disabled
              icon={
                <p className={styles.currency}>
                  {getValues('discountType') === 'fixed' ? getValues('currencyCode') : '%'}
                </p>
              }
              iconPosition='right'
            />
            <ControlledInput
              name='discountPrice'
              type='number'
              label={t('discountPrice')}
              placeholder=''
              disabled
              icon={<p className={styles.currency}>{getValues('currencyCode')}</p>}
              iconPosition='right'
            />
            <DatePickerComponent
              label={t('startDate')}
              name='startDate'
              buttonStyleProp={buttonStyleProp}
              stylesProp={{ marginBottom: 24 }}
              disabled
            />
            <DatePickerComponent
              label={t('expiredDate')}
              name='expiredDate'
              buttonStyleProp={buttonStyleProp}
              stylesProp={{ marginBottom: 24 }}
              disabled
            />
          </section>
          <section className={`${styles.rightSideSection}`}>
            <ImageUploader
              name='imageUrl'
              title={t('imageUrl')}
              path='products'
              disabled
              classNameProp={productRequestData.changedFieldsNames.includes('imageUrl') ? styles.highlight : undefined}
            />
          </section>
        </form>
      </div>
    </FormProvider>
  ) : (
    <p>No such point was found</p>
  );
});
